import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
import { RouterModule } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';
import { CandidateGridComponent } from '../shared/candidate-grid/candidate-grid.component';
import { MyAccountService } from '../account/my-account.service';
import { CandidateCardModel, CandidateSelectionValue } from '../shared/candidate-grid/candidate-card.models';
import { ConfirmationService } from '../shared/confirmation/confirmation.service';
import { ToastService } from '../shared/toast/toast.service';
import { RequestErrorPolicyService } from '../error/request-error-policy.service';
import {
  CipaElectionCandidateDto,
  CipaElectionVoteReceiptDto,
  CipaElectionVotingExperienceDto,
} from './voting.models';
import { VotingService } from './voting.service';

type VotingStep = 1 | 2 | 3;

@Component({
  selector: 'app-voting-steps',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, SkeletonModule, CandidateGridComponent],
  templateUrl: './voting-steps.component.html',
  styleUrls: ['./voting-steps.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VotingStepsComponent implements OnChanges {
  @Input() votingData: CipaElectionVotingExperienceDto | null = null;
  @Input() isLoading = false;
  @Input() showLoadingSkeleton = false;
  hasPasswordConfigured: boolean | null = null;
  @Output() resetRequested = new EventEmitter<void>();

  isSubmitting = false;
  isConfirmingVote = false;
  currentStep: VotingStep = 1;
  voteReceipt: CipaElectionVoteReceiptDto | null = null;
  selectedCandidateIds: CandidateSelectionValue[] = [];
  warningMessage = '';
  errorMessage = '';

  constructor(
    private readonly votingService: VotingService,
    private readonly confirmationService: ConfirmationService,
    private readonly toastService: ToastService,
    private readonly requestErrorPolicy: RequestErrorPolicyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly myAccountService: MyAccountService,
  ) { }

  get maxVotesPerVoter(): number {
    return this.votingData?.election.maxVotesPerVoter ?? 0;
  }

  get candidateCards(): CandidateCardModel[] {
    return this.votingData?.candidates.map((candidate) => ({
      id: candidate.id,
      candidateNumber: candidate.candidateNumber,
      name: candidate.name,
      department: candidate.department,
      position: candidate.position,
      slogan: candidate.slogan,
      photoUrl: candidate.photoUrl,
      yearsAtCompany: candidate.yearsAtCompany,
      isElectionActive: true,
      isElected: false,
    })) ?? [];
  }

  get selectedCandidates(): CipaElectionCandidateDto[] {
    return this.votingData?.candidates.filter((candidate) => this.selectedCandidateIds.includes(candidate.id)) ?? [];
  }

  get hasBlankVoteSelected(): boolean {
    return this.selectedCandidateIds.includes(null);
  }

  get canSubmitVote(): boolean {
    if (this.selectedCandidateIds.length === 0) {
      return false;
    }

    if (this.hasBlankVoteSelected) {
      return this.selectedCandidateIds.length === 1;
    }

    return this.selectedCandidateIds.length <= this.maxVotesPerVoter;
  }

  get submitButtonLabel(): string {
    return 'Registrar minha participação';
  }

  get isVoteInteractionLocked(): boolean {
    return this.isSubmitting || this.isConfirmingVote;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['votingData']) {
      return;
    }

    this.resetLocalExperienceState();
    this.cdr.markForCheck();
  }

  startVoting(): void {
    this.currentStep = 2;
    this.clearMessages();
    this.scrollToStepper();
  }

  returnToIntroduction(): void {
    this.currentStep = 1;
    this.clearMessages();
    this.scrollToStepper();
  }

  resetExperience(): void {
    this.resetRequested.emit();
    this.scrollToBody();
  }

  private resetLocalExperienceState(): void {
    this.currentStep = 1;
    this.voteReceipt = null;
    this.selectedCandidateIds = [];
    this.clearMessages();
    this.isConfirmingVote = false;
    this.isSubmitting = false;
  }

  handleSelectedCandidateIdsChange(candidateIds: CandidateSelectionValue[]): void {
    this.selectedCandidateIds = candidateIds;
    this.clearMessages();
  }

  handleSelectionLimitReached(message: string): void {
    this.warningMessage = message;
    this.errorMessage = '';
  }

  submitVote(): void {
    if (!this.votingData || this.isVoteInteractionLocked) {
      return;
    }

    if (!this.canSubmitVote) {
      this.warningMessage = 'Selecione pelo menos um candidato ou escolha voto em branco antes de registrar sua participação.';
      return;
    }

    const selectedCandidateIds = [...this.selectedCandidateIds];
    const selectedCandidates = this.resolveSelectedCandidates(selectedCandidateIds);

    this.isConfirmingVote = true;
    this.cdr.markForCheck();

    this.confirmationService.yesNo(this.buildVoteConfirmationMessage(selectedCandidates), {
      title: 'Confirmar voto',
      confirmButtonText: 'Confirmar voto',
      cancelButtonText: 'Revisar seleção',
      tone: 'info',
    }).subscribe((confirmed) => {
      this.isConfirmingVote = false;
      this.cdr.markForCheck();

      if (!confirmed) {
        return;
      }

      this.registerVote(selectedCandidateIds);
    });
  }

  private registerVote(selectedCandidateIds: CandidateSelectionValue[]): void {
    if (!this.votingData) {
      return;
    }

    this.isSubmitting = true;
    this.clearMessages();

    this.votingService.submitVote({
      electionId: this.votingData.election.id,
      voterPersonId: this.votingData.voter.personId,
      candidateIds: selectedCandidateIds,
    }).pipe(
      catchError((error) => {
        this.errorMessage = this.requestErrorPolicy.resolveNonHttpMessage(
          error,
          'Não foi possível concluir a votação agora.',
        );

        return of(null);
      }),
      finalize(() => {
        this.isSubmitting = false;
        this.cdr.markForCheck();
      }),
    ).subscribe((receipt) => {
      if (!receipt) {
        return;
      }

      this.voteReceipt = receipt;
      this.currentStep = 3;
      this.toastService.success('Voto computado com sucesso.');

            this.myAccountService.getMyAccountProfile().pipe(
              catchError(() => of(null)),
            ).subscribe((view) => {
              this.hasPasswordConfigured = view?.profile.hasPasswordConfigured ?? null;
              this.cdr.markForCheck();
            });
      this.scrollToStepper();
      this.cdr.markForCheck();
    });
  }

  private buildVoteConfirmationMessage(selectedCandidates: CipaElectionCandidateDto[]): string {
    if (this.hasBlankVoteSelected) {
      return [
        'Você selecionou a opção de voto em branco.',
        '',
        'Ao confirmar, sua participação será registrada sem vincular votos a candidatos.',
        '',
        'Após confirmar, o voto será registrado e não poderá ser alterado. Deseja continuar?',
      ].join('\n');
    }

    const selectedCandidatesList = selectedCandidates
      .map((candidate) => `• ${candidate.candidateNumber} - ${candidate.name}`)
      .join('\n');

    return [
      'Confira os candidatos selecionados antes de concluir sua participação:',
      '',
      selectedCandidatesList,
      '',
      'Após confirmar, o voto será registrado e não poderá ser alterado. Deseja continuar?',
    ].join('\n');
  }

  private resolveSelectedCandidates(selectedCandidateIds: CandidateSelectionValue[]): CipaElectionCandidateDto[] {
    if (!this.votingData) {
      return [];
    }

    const selectedIds = selectedCandidateIds.filter((candidateId): candidateId is number => candidateId !== null);
    return this.votingData.candidates.filter((candidate) => selectedIds.includes(candidate.id));
  }

  private scrollToStepper(): void {
    queueMicrotask(() => {
      const stepper = this.elementRef.nativeElement.querySelector<HTMLElement>('nav.stepper');
      const target = stepper?.offsetParent != null ? stepper : this.elementRef.nativeElement;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private scrollToBody(): void {
    queueMicrotask(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  private clearMessages(): void {
    this.warningMessage = '';
    this.errorMessage = '';
  }
}
